<!-- eslint-disable vue/multi-word-component-names -- Existing component, kept for backward compatibility -->
<template>
    <div class="flex flex-col gap-4">
        <!-- Timeline navigation -->
        <div class="flex justify-center">
            <div class="flex gap-4 max-w-3xl w-full" role="tablist" aria-orientation="horizontal">
                <MomentsTimelineGen1
                    v-for="moment in moments"
                    :key="moment.id"
                    :moment-id="moment.id"
                    :moment-title="moment.title"
                    :date="formatDate(moment.start_date)"
                    :state="moment.state"
                    :is-active="activeMomentId === moment.id"
                    @activate="setActiveMoment" />
            </div>
        </div>

        <!-- Active moment content -->
        <PerformanceMoment v-if="activeMoment" :moment="activeMoment" />

        <NoMomentsNotification v-if="moments.length === 0" />

        <template v-if="organizationCards.length > 0">
            <div>
                <h3 class="title-body mb-10">{{ t("moments.page.organizationTopicsHeading", { organizationName }) }}</h3>
                <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <NewHomeCard
                        v-for="card in organizationCards"
                        :key="card.id"
                        :card-type="card.type"
                        :title="card.title"
                        :subtitle="card.subtitle"
                        :content="card.content"
                        :cta-button-text="card.ctaButtonText"
                        @cta-click="handleOrganizationCardClick(card)">
                        <template #header>
                            <img v-if="card.image_url" :src="card.image_url" :alt="card.title || t('moments.page.cardImageAlt')" class="h-10 w-auto object-contain max-w-[200px]" />
                            <img
                                v-else-if="organizationDetails.logo_url"
                                :src="organizationDetails.logo_url"
                                :alt="organizationDetails.name || t('moments.page.organizationLogoAlt')"
                                class="h-10 w-auto object-contain max-w-[200px]" />
                            <i v-else class="bi bi-people text-3xl/none" aria-hidden="true"></i>
                        </template>
                    </NewHomeCard>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup>
import NewHomeCard from "~vue/ChatHome/NewHomeCard.vue";
import MomentsTimelineGen1 from "~vue/components/moments/MomentsTimelineGen1.vue";
import NoMomentsNotification from "~vue/Moments/NoMomentsNotification.vue";
import PerformanceMoment from "~vue/Moments/PerformanceMoment.vue";
import { openUrl } from "~vue/utils";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHATSOURCE, HOMEPAGE_CARD_LOCATION } from "~vue/utils/constants";
import { formatDate as localizedDate } from "~vue/utils/formatDate";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, ref } from "vue";
import { useI18n } from "vue-i18n";

const { moments } = defineProps({
    moments: {
        type: Array,
        default: () => [],
    },
    organizationCards: {
        type: Array,
        default: () => [],
    },
});

const { t } = useI18n();

const getInitialMomentSelection = () => {
    // Deep link from the homepage banner's per-moment buttons (ENG-1330)
    const momentIdParam = new URLSearchParams(globalThis.location.search).get("moment_id");
    if (momentIdParam) {
        const requestedMoment = moments.find((m) => m.id === Number(momentIdParam) && m.state !== "locked");
        if (requestedMoment) {
            return requestedMoment;
        }
    }
    const latestMoments = [...moments].reverse();
    return latestMoments.find((m) => m.state === "open") || latestMoments.find((m) => m.state === "completed") || latestMoments.find((m) => m.state === "expired") || moments[0];
};
const defaultActiveMoment = getInitialMomentSelection();
const activeMomentId = ref(defaultActiveMoment?.id || null);

const setActiveMoment = (momentId) => {
    activeMomentId.value = momentId;
};

const activeMoment = computed(() => {
    return moments.find((m) => m.id === activeMomentId.value);
});

const formatDate = (date) => {
    if (!date) {
        return "";
    }
    return localizedDate(date, "month-year");
};

const organizationDetails = inject("organization_details");
const organizationName = computed(() => {
    {
        if (organizationDetails.value?.is_default || !organizationDetails.value?.name) {
            return t("moments.page.defaultOrganizationName");
        }
        return organizationDetails.value.name;
    }
});
const handleOrganizationCardClick = (card) => {
    logUserInteraction(AmplitudeEventNames.HOMEPAGE_CARD_CLICKED, { card_type: card.type, location: HOMEPAGE_CARD_LOCATION.MOMENTS });
    openUrl(card.url, { queryParams: { chat_source: CHATSOURCE.TRENDING_ORG_PROMPT } });
};
</script>
